import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  TrustAnalysis,
  TrustAnalysisDisplayStatus,
  TrustAnalysisParty,
  TrustAnalysisPartyRole,
  trustAnalysisDisplayStatus,
  trustAnalysisPartiesByRole,
  trustAnalysisPartyName,
  trustAnalysisStatusLabel,
  trustAnalysisTrustTypeLabel,
} from '@portal-workspace/grow-shared-library';
import { MessageBoxComponent } from '../message-box/message-box.component';

/** One extracted role with its parties, as rendered; see trustAnalysisPartiesByRole. */
interface TrustPartyGroup {
  role: TrustAnalysisPartyRole;
  label: string;
  parties: TrustAnalysisParty[];
}

/**
 * Identifies the trust the way a reviewer names it — the trust and who acts for it — so two trusts
 * on the one application are told apart while both headers are collapsed. Falls back to the file
 * name until the analysis returns a name, which is all there is to show while it runs.
 */
const buildSubtitle = (analysis: TrustAnalysis): string => {
  const trustName = analysis.result?.trustName;
  if (!trustName) {
    return analysis.fileName;
  }
  const trustees = (analysis.result?.parties ?? [])
    .filter(party => party.role === 'TRUSTEE')
    .map(trustAnalysisPartyName);
  return trustees.length ? `${trustName} | ${trustees.join(', ')}` : trustName;
};

/**
 * The FrankieOne Trust Analyzer result for one trust entity, rendered in the same idiom as the KYB
 * result block it sits above: a header bar that stays readable while collapsed, and a fact grid
 * plus tables underneath.
 *
 * Collapsed by default. Unlike the KYB block this is not the reason the row was opened, and a deed
 * with a dozen beneficiaries would otherwise push the KYB result off screen.
 */
@Component({
  selector: 'trust-analysis-block',
  templateUrl: './trust-analysis-block.component.html',
  styleUrls: ['./trust-analysis-block.component.scss'],
  standalone: true,
  imports: [
    DatePipe,
    NgClass,
    MatButtonModule,
    MatMenuModule,
    MatTableModule,
    MatTooltipModule,
    MessageBoxComponent,
  ],
})
export class TrustAnalysisBlockComponent {
  // Derived collections are computed on assignment rather than in getters: mat-table re-renders
  // whenever its dataSource reference changes, so a getter returning a fresh array would re-render
  // the party tables on every change-detection cycle.
  @Input({ required: true })
  set analysis(value: TrustAnalysis) {
    this._analysis = value;
    this.displayStatus = trustAnalysisDisplayStatus(value.status);
    this.statusLabel = trustAnalysisStatusLabel(value.status);
    this.partyGroups = trustAnalysisPartiesByRole(value.result?.parties ?? []);
    this.subtitle = buildSubtitle(value);
  }

  get analysis(): TrustAnalysis {
    return this._analysis;
  }

  private _analysis!: TrustAnalysis;

  /**
   * File name and upload time, shown when an entity has more than one deed so the blocks stay
   * distinguishable while collapsed.
   */
  @Input() identity: string | null = null;

  /**
   * Every analysis of this deed condition, newest first, including the one on display.
   *
   * A condition holds a deed and any later amendments to it, each separately analysed. They are
   * offered as versions of one block rather than as blocks of their own so the panel always shows a
   * single trust structure, and the reviewer chooses which deed that structure came from.
   */
  @Input() versions: TrustAnalysis[] = [];

  @Output() retry: EventEmitter<TrustAnalysis> = new EventEmitter<TrustAnalysis>();
  @Output() downloadDeed: EventEmitter<TrustAnalysis> = new EventEmitter<TrustAnalysis>();
  @Output() selectVersion: EventEmitter<TrustAnalysis> = new EventEmitter<TrustAnalysis>();

  displayStatus: TrustAnalysisDisplayStatus = 'pending';
  statusLabel = '';
  subtitle = '';
  partyGroups: TrustPartyGroup[] = [];

  expanded = false;
  partyColumns = ['name', 'kind', 'detail', 'pages'];

  trustTypeLabel = trustAnalysisTrustTypeLabel;
  partyName = trustAnalysisPartyName;

  toggle() {
    this.expanded = !this.expanded;
  }

  /** Versions are newest first, so anything but the first is a deed a later one replaced. */
  get superseded(): boolean {
    return this.versions.length > 1 && this.versions[0].runId !== this.analysis.runId;
  }

  isCurrentVersion(version: TrustAnalysis): boolean {
    return version.runId === this.analysis.runId;
  }

  /** Upload date, and which entry is the deed as it now stands, so switching back is one click. */
  versionMeta(version: TrustAnalysis): string {
    const uploaded = new Date(version.createdAt).toLocaleDateString();
    return this.versions[0]?.runId === version.runId ? `${uploaded} · Latest` : uploaded;
  }

  get failed(): boolean {
    return this.displayStatus === 'failed';
  }

  get pending(): boolean {
    return this.displayStatus === 'pending';
  }

  /**
   * Why the analysis is not usable, in the order a reviewer can act on it: our own failure first,
   * then FrankieOne's extraction error, then a deed whose type it could not classify.
   */
  get problem(): string | null {
    const result = this.analysis.result;
    if (this.failed) {
      return result?.errorMessage || this.analysis.lastError || this.statusLabel;
    }
    if (result?.detectedTrustType === 'UNKNOWN') {
      return 'The trust type could not be determined from this deed. Check the deed and correct the '
        + 'type before relying on the extracted structure.';
    }
    if (result?.analysisStatus === 'STALE') {
      return 'This analysis was superseded when the deed was replaced. Re-run the analysis to refresh it.';
    }
    return null;
  }

  /** Whatever identifies a party beyond its name: date of birth, registration, or address. */
  partyDetail(party: TrustAnalysisParty): string {
    const parts: string[] = [];
    if (party.dateOfBirth) parts.push(`DOB ${party.dateOfBirth}`);
    if (party.abn) parts.push(`ABN ${party.abn}`);
    if (party.acn) parts.push(`ACN ${party.acn}`);
    if (party.unitsHeld != null) parts.push(`${party.unitsHeld} units`);
    if (party.unitPercentage != null) parts.push(`${party.unitPercentage}%`);
    if (party.unstructuredAddress) parts.push(party.unstructuredAddress);
    return parts.length ? parts.join(' · ') : '—';
  }

  partyKind(party: TrustAnalysisParty): string {
    switch (party.partyType) {
      case 'INDIVIDUAL':
        return 'Individual';
      case 'ORGANIZATION':
        return 'Company';
      case 'TEXT':
        return 'Described in deed';
      default:
        return 'Unknown';
    }
  }

  /** Deed pages a party was read from, so the reviewer can check it against the source document. */
  partyPages(party: TrustAnalysisParty): string {
    return party.sourcePages.length ? party.sourcePages.join(', ') : '—';
  }

  displayValue(value: string | number | null | undefined): string {
    if (value == null || value === '') {
      return '—';
    }
    return String(value);
  }

  fileSizeLabel(bytes: number | null): string {
    if (bytes == null) return '';
    const mb = bytes / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }
}
